import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';
import { normalizeProfileImageUrl } from '../utils/imageHelper';

const UsersContainer = styled.div`
  padding: 0 5% 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 12px 15px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  color: var(--primary-color);
  font-size: 1rem;
  width: 300px;
  
  &:focus {
    outline: none;
    border-color: var(--gradient-pink);
  }
  
  &::placeholder {
    color: #636e72;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 15px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  color: var(--primary-color);
  font-size: 1rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: var(--gradient-pink);
  }
`;

const UsersTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    color: var(--primary-color);
`;

const Th = styled.th`
    text-align: left;
    padding: 15px;
    border-bottom: 2px solid var(--gradient-pink);
`;

const Td = styled.td`
    padding: 15px;
    border-bottom: 1px solid var(--border-color);
    vertical-align: middle;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 10px;
`;

const DeleteButton = styled.button`
    padding: 8px 15px;
    border-radius: 5px;
    background: #e94057;
    color: white;
    border: none;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-2px);
        background: #ff3742;
    }
`;

const LoadingText = styled.div`
    text-align: center;
    padding: 50px;
    color: var(--secondary-color);
    font-size: 1.2rem;
`;

const ErrorText = styled.div`
    text-align: center;
    padding: 50px;
    color: #e94057;
    font-size: 1.2rem;
`;

const RetryButton = styled.button`
    padding: 10px 20px;
    background: var(--gradient-pink);
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 600;
    margin-top: 10px;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-2px);
    }
`;

const AdminUsersPage = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');

    useEffect(() => {
        fetchUsers();
    }, []);

    // Arama ve filtreleme
    useEffect(() => {
        let result = users;
        
        // Arama filtresi
        if (searchTerm) {
            result = result.filter(user => 
                user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Rol filtresi
        if (roleFilter !== 'all') {
            result = result.filter(user => user.role === roleFilter);
        }
        
        setFilteredUsers(result);
    }, [users, searchTerm, roleFilter]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch(getApiUrl(API_ENDPOINTS.GET_USERS), {
                credentials: 'include'
            });
            
            const responseText = await response.text();
            
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseError) {
                throw new Error('API geçersiz JSON döndürüyor');
            }
            
            if (!response.ok) {
                throw new Error(data.error || `HTTP error! status: ${response.status}`);
            }
            
            if (data.success && Array.isArray(data.users)) {
                setUsers(data.users);
            } else if (Array.isArray(data)) {
                setUsers(data);
            } else {
                throw new Error(data.error || 'Geçersiz veri formatı');
            }
            
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            setUsers(prev => prev.map(user => 
                user.id === userId ? { ...user, role: newRole } : user
            ));
            
            const response = await fetch(getApiUrl(API_ENDPOINTS.UPDATE_USER_ROLE), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ 
                    user_id: userId,
                    role: newRole 
                })
            });

            if (!response.ok) {
                throw new Error('Rol güncellenemedi');
            }

            try {
                const result = await response.json();
                if (!result.success) {
                    throw new Error(result.error || 'Rol güncellenemedi');
                }
            } catch (jsonError) {
                // Boş response olabilir, devam et
            }
            
        } catch (error) {
            alert('Rol güncellenirken hata oluştu: ' + error.message);
            fetchUsers(); // Listeyi yeniden yükle
        }
    };

    const handleDeleteUser = async (userId, username) => {
        if (window.confirm(`"${username}" kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!`)) {
            try {
                const response = await fetch(getApiUrl(API_ENDPOINTS.DELETE_USER), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({ user_id: userId })
                });

                if (!response.ok) {
                    throw new Error('Kullanıcı silinemedi');
                }

                setUsers(prev => prev.filter(user => user.id !== userId));
                alert('Kullanıcı başarıyla silindi!');
                
            } catch (error) {
                alert('Kullanıcı silinirken hata oluştu: ' + error.message);
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Bilinmiyor';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return 'Geçersiz tarih';
        }
    };

    if (loading) {
        return (
            <UsersContainer>
                <Header>
                    <Title>Admin Paneli</Title>
                </Header>
                <LoadingText>Kullanıcılar yükleniyor...</LoadingText>
            </UsersContainer>
        );
    }

    return (
        <UsersContainer>
            <Header>
                <Title>Üye Yönetimi</Title>
                <div style={{ fontSize: '1rem', color: '#b2bec3' }}>
                    Toplam {filteredUsers.length} kullanıcı
                </div>
            </Header>

            {error && (
                <ErrorText>
                    {error}
                    <br />
                    <RetryButton onClick={fetchUsers}>
                        Tekrar Dene
                    </RetryButton>
                </ErrorText>
            )}

            <SearchContainer>
                <SearchInput
                    type="text"
                    placeholder="Kullanıcı adı veya email ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FilterSelect
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                >
                    <option value="all">Tüm Roller</option>
                    <option value="user">Kullanıcı</option>
                    <option value="moderator">Moderatör</option>
                    <option value="admin">Admin</option>
                </FilterSelect>
            </SearchContainer>

            <UsersTable>
                <thead>
                    <tr>
                        <Th>Kullanıcı</Th>
                        <Th>Email</Th>
                        <Th>Rol</Th>
                        <Th>Kayıt Tarihi</Th>
                        <Th>Durum</Th>
                        <Th>İşlemler</Th>
                    </tr>
                </thead>
                <tbody>
                    {filteredUsers.map(user => (
                        <tr key={user.id}>
                            <Td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <img 
                                        src={normalizeProfileImageUrl(user.avatar_url) || `https://ui-avatars.com/api/?name=${user.username}&background=random&color=fff`} 
                                        alt={user.username}
                                        style={{ 
                                            width: '40px', 
                                            height: '40px', 
                                            borderRadius: '50%',
                                            objectFit: 'cover'
                                        }}
                                        onError={(e) => {
                                            e.target.src = `https://ui-avatars.com/api/?name=${user.username}&background=random&color=fff`;
                                        }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: '600' }}>{user.username}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#b2bec3' }}>
                                            ID: {user.id}
                                        </div>
                                    </div>
                                </div>
                            </Td>
                            <Td>{user.email}</Td>
                            <Td>
                                <select 
                                    value={user.role || 'user'} 
                                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '5px',
                                        background: 'rgba(0,0,0,0.3)',
                                        color: 'white',
                                        border: '1px solid var(--border-color)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value="user">Kullanıcı</option>
                                    <option value="moderator">Moderatör</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </Td>
                            <Td>{formatDate(user.created_at)}</Td>
                            <Td>
                                <div style={{ 
                                    padding: '4px 8px', 
                                    borderRadius: '4px', 
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    background: user.is_active ? '#00b894' : '#e17055',
                                    color: 'white',
                                    display: 'inline-block'
                                }}>
                                    {user.is_active ? 'Aktif' : 'Pasif'}
                                </div>
                            </Td>
                            <Td>
                                <ActionButtons>
                                    <DeleteButton onClick={() => handleDeleteUser(user.id, user.username)}>
                                        Sil
                                    </DeleteButton>
                                </ActionButtons>
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </UsersTable>
            
            {filteredUsers.length === 0 && !error && (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '50px', 
                    color: '#b2bec3',
                    fontSize: '1.2rem'
                }}>
                    {searchTerm || roleFilter !== 'all' ? 'Arama kriterlerinize uygun kullanıcı bulunamadı.' : 'Henüz hiç kullanıcı bulunmuyor.'}
                </div>
            )}
        </UsersContainer>
    );
};

export default AdminUsersPage;
